import { compact, isEmpty } from 'lodash';
import { useCallback } from 'react';
import { FieldValues, useWatch } from 'react-hook-form';

import {
  ExtendedTypeaheadQueryOpts,
  useAsyncTypeaheadProps,
} from '@/components/form/baseInputs/BackendTypeaheadSelectInput/hooks/useAsyncTypeaheadProps';
import { FormAwareTypeaheadMultiSelectInputProps } from '@/components/form/formAwareInputs/FormAwareTypeaheadMultiSelectInput';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useFormContext } from '@/components/react-hook-form';

import {
  GetClientsMultipleQuery,
  GetClientsMultipleQueryVariables,
  useGetClientsMultipleQuery,
} from '../graphql/ClientsTypeahead.generated';

type ClientsTypeaheadProps<FormShape extends FieldValues> = Omit<
  FormAwareTypeaheadMultiSelectInputProps<FormShape>,
  'options' | 'inputValue' | 'onInputChange'
>;

export function useClientsTypeahead<FormShape extends FieldValues>(
  fieldName: ClientsTypeaheadProps<FormShape>['fieldName']
) {
  const { createErrorFeedback } = useFeedback();
  const { control } = useFormContext<FormShape>();

  // Watch currently-selected ids so we can ask the backend for them
  const selectedIds = useWatch({ control, name: fieldName }) as string[];

  // Map the user's search text => GraphQL variables for queryData
  const searchTermToVariables = useCallback(
    (search: string) => ({
      query: search,
    }),
    []
  );

  // Always ask for the full records of already-selected items
  const getSelectedOptionVariables = useCallback(
    () => ({
      selectedOptionsLike: {
        idIn: isEmpty(selectedIds) ? [''] : compact(selectedIds),
      },
    }),
    [selectedIds]
  );

  // Convert the GQL results into <Select> options
  const toOptions: ExtendedTypeaheadQueryOpts<
    string,
    GetClientsMultipleQuery,
    GetClientsMultipleQueryVariables
  >['toOptions'] = useCallback(
    (rows) =>
      rows.map((h) => ({
        value: h.id,
        display: h.displayName,
        secondaryText: h.primaryRelationshipOwner?.displayName ?? undefined,
      })),
    []
  );

  const [typeaheadProps, queryResult] = useAsyncTypeaheadProps(
    useGetClientsMultipleQuery,
    {
      searchTermToVariables,
      getSelectedOptionVariables,
      toOptions,
      pageSize: 50,
      onError: createErrorFeedback(
        'Failed to fetch clients. Please refresh the page to try again.'
      ),
      unselectedGroupLabel: 'Available clients',
      skipAutocompleteResetClear: true,
    }
  );

  return { typeaheadProps, queryResult };
}
